/* eslint-disable react-hooks/exhaustive-deps */
import { Form } from "antd";
import { CARRIER_METHOD_LABEL, SHIPPING_TYPE } from "features/Cart/constants";
import { PAYMENT_METHOD } from "config/constants";
import { getCartOrderValue, getInvalidShippingTypeMessage, getShippingTypeOptions } from "features/Cart/shippingPolicy";
import { convertGetCart } from "features/Cart/utils";
import { isMaterial } from "features/Products/helper";
import { useFlags } from "flagsmith/react";
import {
  useCartPermissions,
  useGetAllCarts,
  useGetCart,
  useGetShippingProviderServiceFee,
  useGetShippingTypes,
  useUpdateCartItemV2,
  useUpdateCartSeller,
  useUpdateCartV2,
} from "hooks/cart";
import { useGetInfoPlatform } from "hooks/info-platform";
import { isEmpty, isNil, omit, pick } from "lodash";
import { VAT_TYPE } from "pages/App/Product/constants";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getQuery, notify, parseIsoDateStringToMoment } from "utils/helperFuncs";
import { renameKeys } from "utils/object";

const useList = ({ useInOrder }) => {
  const [form] = Form.useForm();

  const { id } = useParams();
  const GET_QUERY = getQuery();

  const [cartID, setCartID] = useState(id);

  // `enable_new_shipping_provider` — cơ chế "v2 shipping provider" CHUNG (servicesFee/TreeSelect),
  // dùng chung với 5 màn đổi shipper/chia đơn khác. Đây là nơi DUY NHẤT trên màn giỏ hàng còn gọi
  // `servicesFee`; khối Hoá đơn đã chuyển sang đọc thẳng `cart.shippingFee`.
  const flags = useFlags(["enable_new_shipping_provider"]);
  const idEnableNewShippingProvider = flags.enable_new_shipping_provider.enabled;

  const { loadingCartPermissions, cartPermissions } = useCartPermissions();
  const { data, loading: loadingGetCart, refetch: refetchCart } = useGetCart({ id: cartID || GET_QUERY?.id });
  const [isEmptyCart, setIsEmptyCart] = useState(true);
  const { handleUpdateCartV2, loading: loadingUpdateCart } = useUpdateCartV2();
  const { data: carts, loading: loadingGetAllCarts } = useGetAllCarts();
  const { handleUpdateCartSeller, loading: loadingUpdateCartSeller } = useUpdateCartSeller();
  const { handleUpdateCartItemV2, loading: loadingUpdateCartItem } = useUpdateCartItemV2();
  const onChangeTab = (value) => setCartID(value);

  const [isChangeShippingType, setIsChangeShippingType] = useState(false);

  const cartConverter = convertGetCart(data);
  const {
    data: dataShippingProvider,
    loading: loadingShippingProvider,
    refetch: refetchShippingProvider,
  } = useGetShippingProviderServiceFee({
    shippingType: data?.shippingType,
    serviceFeeInput: { cartID: cartID },
    // Đợi `GetCart` load xong mới gọi — tránh bắn request ngay khi mới biết `cartID` từ URL, trước
    // cả khi cart data sẵn sàng.
    skip: !data,
  });

  // Ref "luôn mới nhất" cho `dataShippingProvider` — để MAIN EFFECT bên dưới không cần liệt kê nó
  // trong dependency array (tránh refetch cart thêm 1 lần chỉ vì provider vừa load xong) mà
  // `renderShippingProvider` vẫn đọc được giá trị mới nhất.
  const dataShippingProviderRef = useRef(dataShippingProvider);
  dataShippingProviderRef.current = dataShippingProvider;

  // Danh sách PTVC khả dụng thật của BE (zone + ngưỡng giá trị đơn) — nguồn DUY NHẤT để biết PTVC
  // đang chọn có bị disable hay không, FE không tự tính ngưỡng. Chờ cart load xong mới gọi
  // (`skip: !data`), tránh 1 request "rác" với cityID/warehouseID/subTotal rỗng lúc mount.
  const { data: shippingTypes } = useGetShippingTypes({
    contactCityID: data?.shippingContact?.cityID,
    contactWardID: data?.shippingContact?.wardID,
    exportedWarehouseID: data?.exportedWarehouse?.id,
    subTotal: getCartOrderValue(data),
    skip: !data,
  });

  // Popup "PTVC không phù hợp" hiện NGAY khi giá trị đơn đổi khiến PTVC đang chọn hết hợp lệ, không
  // chỉ chờ tới lúc bấm "Tạo đơn". Chỉ hiện 1 lần cho mỗi lần rớt invalid — reset cờ khi PTVC hợp
  // lệ trở lại.
  const [invalidShippingTypePopup, setInvalidShippingTypePopup] = useState({ visible: false });
  const hasNotifiedInvalidShippingTypeRef = useRef(false);
  const closeInvalidShippingTypePopup = () => setInvalidShippingTypePopup({ visible: false });

  const convertItemsForForm = (items, sellerDebtAge) => {
    return items?.map((item) => ({
      id: item.id,
      quantity: item?.quantity,
      vat: item?.vat,
      maxDaysOfDebt: isNil(item?.maxDaysOfDebt) ? (sellerDebtAge > 0 ? sellerDebtAge : undefined) : item?.maxDaysOfDebt,
      customerPrice: item?.customerPrice,
      customSellerPrice: item?.price,
      productWarranty: item?.productWarranty,
      productType: item?.product?.productType,
    }));
  };

  // BE lưu phí giao nội thành vào `cart.shippingFee` nhưng trả `urbanConfig` rỗng, trong khi ô "Nhập
  // phí vận chuyển" bind vào `urbanConfig.fee` — không seed ngược lại thì F5 xong ô trắng và validate
  // chặn tạo đơn dù phí đã lưu.
  const buildShippingConfigForForm = (cart, carrierMethodShipping, providerIDShipping) => ({
    ...cart?.shippingConfiguration,
    deliver3PartyConfig: {
      ...cart?.shippingConfiguration?.deliver3PartyConfig,
      providerID: renderShippingProvider(carrierMethodShipping, providerIDShipping),
    },
    urbanConfig: {
      ...cart?.shippingConfiguration?.urbanConfig,
      fee:
        cart?.shippingConfiguration?.urbanConfig?.fee ??
        (cart?.shippingType === SHIPPING_TYPE.URBAN_COD ? cart?.shippingFee : undefined),
    },
  });

  const renderShippingProvider = (carrierMethodShipping, providerIDShipping) => {
    const dataShippingProvider = dataShippingProviderRef.current;
    const getShippingMethods = dataShippingProvider?.find(
      (item) => item?.method === carrierMethodShipping && item?.carrierMinFee !== null && item?.carrierMaxFee !== null
    );

    const getShippingProvider = getShippingMethods?.providers?.find(
      (item) => item.id === `${providerIDShipping} - ${carrierMethodShipping}`
    );

    const checkShipping = () => {
      if (carrierMethodShipping !== null && providerIDShipping !== null) {
        return getShippingProvider;
      } else if (carrierMethodShipping !== null && providerIDShipping === null) {
        return getShippingMethods;
      }
    };
    if (idEnableNewShippingProvider) {
      if (
        !dataShippingProvider ||
        !checkShipping() ||
        checkShipping() === null ||
        !data?.items ||
        !data?.items?.length === 0
      ) {
        return null;
      } else {
        if (!isChangeShippingType) {
          if (providerIDShipping === null) {
            return carrierMethodShipping;
          } else {
            return `${providerIDShipping} - ${carrierMethodShipping}`;
          }
        } else {
          return undefined;
        }
      }
    } else {
      return providerIDShipping;
    }
  };

  const [cartUpdateSeller, setCartUpdateSeller] = useState([]);
  const isUpdateSellerRef = useRef(false);

  useEffect(() => {
    if (isUpdateSellerRef.current) {
      isUpdateSellerRef.current = false;
      return;
    }
    // `refetch()` trên Apollo Client 3.5.10 KHÔNG tôn trọng `skip` của `useGetCart` — gọi nó khi
    // chưa có `cartID`/`GET_QUERY?.id` hợp lệ sẽ ép query chạy với `id: undefined`, BE trả lỗi.
    // Guard lại để effect này chỉ refetch khi đã có id thật.
    if (!cartID && !GET_QUERY?.id) {
      return;
    }
    // Khi `GET_QUERY?.id` VỪA đổi, `useGetCart` đã tự fetch rồi (`loadingGetCart` đang `true`) —
    // gọi thêm `refetchCart()` ở đây là ép chạy lại LẦN 2 dư thừa. Đợi `loadingGetCart` về `false`
    // rồi mới refetch.
    if (loadingGetCart) {
      return;
    }
    refetchCart().then(async (res) => {
      const cart = await res?.data?.cart?.get;
      const carrierMethodShipping = cart?.shippingConfiguration?.deliver3PartyConfig?.carrierMethod;
      const providerIDShipping = cart?.shippingConfiguration?.deliver3PartyConfig?.providerID;
      const sellerDebtAge = cart?.seller?.debtAge;
      const convertedItems = convertItemsForForm(cart?.items, sellerDebtAge);
      setCartUpdateSeller(cart?.seller?.warehouses);
      form.setFieldsValue({
        ...cart,
        shippingConfiguration: buildShippingConfigForForm(cart, carrierMethodShipping, providerIDShipping),
        sellerID: [cart?.seller?.code, cart?.seller?.fullName, cart?.seller?.telephone].join(" - "),
        items: convertedItems,
        deliveryDeadline: cart?.deliveryDeadline && parseIsoDateStringToMoment(cart?.deliveryDeadline),
      });

      // PTVC đang chọn không còn hợp lệ → tự chuyển sang PTVC enable đầu tiên theo thứ tự đang hiển thị
      // và LƯU LUÔN xuống BE — không để trống vì `shippingType` là field NonNull trên
      // `UpdateCartInput` (BE trả lỗi "cannot be null" khi gửi null).
      const shippingTypeOptionsForCart = getShippingTypeOptions(shippingTypes);
      const invalidShippingTypeOption = shippingTypeOptionsForCart?.find(
        (option) => option?.value === cart?.shippingType && option?.disabled
      );
      if (invalidShippingTypeOption) {
        const fallbackShippingType = shippingTypeOptionsForCart?.find((option) => !option?.disabled)?.value;
        form.setFieldsValue({ shippingType: fallbackShippingType });
        // Chỉ thử persist ĐÚNG 1 LẦN mỗi episode invalid (dùng chung `hasNotifiedInvalidShippingTypeRef`)
        // — nếu BE không thực sự đổi được `shippingType` (vd. PICK_UP_COUNTER thiếu kho/người nhận),
        // effect lặp lại vô hạn nếu không chặn. Thất bại thì dừng, để Sale tự chọn lại qua popup.
        if (!hasNotifiedInvalidShippingTypeRef.current) {
          hasNotifiedInvalidShippingTypeRef.current = true;
          setInvalidShippingTypePopup({
            visible: true,
            message: getInvalidShippingTypeMessage({ cart, shippingTypeName: invalidShippingTypeOption?.label }),
          });
          // Chờ `useGetCart` load xong lần đầu mới persist, tránh gọi `handleUpdate` khi cart
          // chưa sẵn sàng.
          if (fallbackShippingType && !loadingGetCart) {
            try {
              await handleUpdate({ shippingType: fallbackShippingType });
            } catch (error) {
              notify.error({ message: error.message });
            }
          }
        }
      } else {
        hasNotifiedInvalidShippingTypeRef.current = false;
      }

      const hasItemsWithDebtAgeFilled = cart?.items?.some((item) => isNil(item?.maxDaysOfDebt) && sellerDebtAge > 0);
      if (hasItemsWithDebtAgeFilled) {
        await handleUpdateCartItemV2({
          cartID: cartID || GET_QUERY?.id,
          request: {
            cartItems: convertedItems?.map((item) => converterRequestCartItem(item)),
          },
        });
      }
    });
    // CỐ TÌNH KHÔNG đưa `data` vào deps: effect gọi `refetchCart()` (cập nhật `data`) nên sẽ tự kích
    // lại chính nó nếu `data` là dep, tạo vòng lặp vô hạn. Cũng không đưa `dataShippingProvider` —
    // đọc qua `dataShippingProviderRef` là đủ.
  }, [GET_QUERY?.id, loadingGetCart, JSON.stringify(shippingTypes)]);

  const converterRequestCartItem = (item) =>
    omit(
      {
        ...item,
        customerPrice: isMaterial(item?.productType) ? item?.customerPrice : item?.customSellerPrice,
        quantity: isNil(item?.quantity) ? 1 : item?.quantity,
      },
      ["productType"]
    );

  const getApplyLoyaltyCashByPaymentMethod = (paymentMethod) => {
    if (paymentMethod === PAYMENT_METHOD.Debt || paymentMethod === PAYMENT_METHOD.COD) {
      return false;
    }

    return undefined;
  };

  const dataInfoAgent = pick(cartConverter, [
    "shippingType",
    "vatAllOrder",
    "sender",
    "sellerID",
    "allSenders",
    "shippingContact",
    "shippingConfiguration",
    "seller",
    "exportedWarehouse",
    "orderPriority",
    "deliveryDeadline",
    "warehouses",
    "listProducts",
    "temporaryTotal",
    "operationInfo",
  ]);

  const dataInfoProductAndBill = {
    ...pick(cartConverter, [
      "id",
      "listProducts",
      "paymentMethod",
      "customerPaymentMethod",
      "temporaryTotal",
      "customerSubTotal",
      "shippingFee",
      "operationFee",
      "operationInfo",
      "totalServiceProduct",
      "discountAmount",
      "total",
      "customerTotal",
      "customerSubTotal",
      "orderType",
      "seller",
      "total",
      "paymentByDebit",
      "paymentByCash",
      "loyaltyCashInfo",
      "loyaltyCashDiscount",
      "sellerID",
      "exportedWarehouseID",
      "vatAllOrder",
      "shippingType",
      "shippingConfiguration",
    ]),
    id: cartID,
  };
  const handleUpdate = async (value, minPrice, maxPrice, fee, parentMethodValue) => {
    if (value && "shippingType" in value) {
      setIsChangeShippingType(true);
    } else {
      setIsChangeShippingType(false);
    }

    const formData = form.getFieldsValue();

    const {
      senderAddress,
      senderName,
      senderPhone,
      shippingContact,
      vatAllOrder,
      shippingConfiguration,
      exportedWarehouse,
    } = data;

    const getShippingMethods = dataShippingProvider?.find(
      (item) => item?.method === shippingConfiguration?.deliver3PartyConfig?.carrierMethod
    );
    let deliver3PartyConfig;
    const isProviderIDValid = Object.values(CARRIER_METHOD_LABEL).includes(
      CARRIER_METHOD_LABEL[
        formData?.shippingConfiguration?.deliver3PartyConfig?.providerID
          ? formData?.shippingConfiguration?.deliver3PartyConfig?.providerID
          : shippingConfiguration?.deliver3PartyConfig?.providerID
      ]
    );

    if (idEnableNewShippingProvider) {
      if (isProviderIDValid) {
        deliver3PartyConfig = {
          carrierMethod: formData?.shippingConfiguration?.deliver3PartyConfig?.providerID
            ? formData?.shippingConfiguration?.deliver3PartyConfig?.providerID
            : shippingConfiguration?.deliver3PartyConfig?.providerID,
          carrierMaxFee: maxPrice >= 0 ? maxPrice : getShippingMethods?.carrierMaxFee || 0,
          carrierMinFee: minPrice >= 0 ? minPrice : getShippingMethods?.carrierMinFee || 0,
          fee: minPrice,
        };
      } else {
        deliver3PartyConfig = {
          providerID:
            formData?.shippingConfiguration?.deliver3PartyConfig?.providerID?.split(" - ")[0] ||
            shippingConfiguration?.deliver3PartyConfig?.providerID,
          fee: fee >= 0 ? fee : shippingConfiguration?.deliver3PartyConfig?.fee,
          carrierMaxFee: fee >= 0 ? fee : shippingConfiguration?.deliver3PartyConfig?.carrierMaxFee || 0,
          carrierMinFee: fee >= 0 ? fee : shippingConfiguration?.deliver3PartyConfig?.carrierMinFee || 0,
          carrierMethod: parentMethodValue || shippingConfiguration?.deliver3PartyConfig?.carrierMethod,
        };
      }
    } else {
      deliver3PartyConfig = { providerID: formData?.shippingConfiguration?.deliver3PartyConfig?.providerID };
    }

    const shippingConfigurationRename = renameKeys(
      { urbanConfig: "urbanCODConfig" },
      {
        busConfig: {
          busName: formData?.shippingConfiguration?.busConfig?.busName,
          busStation: formData?.shippingConfiguration?.busConfig?.busStation,
          telephone: formData?.shippingConfiguration?.busConfig?.telephone,
          location: !idEnableNewShippingProvider ? formData?.shippingConfiguration?.busConfig?.location : undefined,
        },
        deliver3PartyConfig: deliver3PartyConfig,
        urbanConfig: {
          fee: formData?.shippingConfiguration?.urbanConfig?.fee
            ? formData?.shippingConfiguration?.urbanConfig?.fee
            : shippingConfiguration?.urbanConfig?.fee,
        },
      }
    );

    const renameFieldsForm = renameKeys(
      { shippingConfiguration: "shippingConfig" },
      {
        ...formData,
        senderAddress,
        senderName,
        senderPhone,
        exportedWarehouseID:
          cartUpdateSeller?.length > 0 && isUpdateSellerRef.current ? cartUpdateSeller[0].id : exportedWarehouse?.id,
        shippingContactID: shippingContact?.id,
        shippingConfiguration: { ...shippingConfigurationRename },
        vatAllOrder,
        discountAmount: isNil(formData?.discountAmount) ? 0 : formData?.discountAmount,
      }
    );
    const dataMissingUseInEdit = isEmpty(useInOrder)
      ? {}
      : {
          ...pick(data, ["orderPriority", "fullStockRequired"]),
          exportedWarehouseID:
            cartUpdateSeller?.length > 0 && isUpdateSellerRef.current
              ? cartUpdateSeller[0].id
              : data?.exportedWarehouse?.id,
        };
    const omitFields = omit(renameFieldsForm, ["sellerID", "items", "exportedWarehouse", "vat"]);
    const applyLoyaltyCashByPaymentMethod = getApplyLoyaltyCashByPaymentMethod(value?.paymentMethod);

    const request = {
      ...omitFields,
      ...value,
      ...(applyLoyaltyCashByPaymentMethod === undefined
        ? {}
        : {
            applyLoyaltyCash: applyLoyaltyCashByPaymentMethod,
          }),
      ...dataMissingUseInEdit,
    };

    const params = { cartID, request };
    await handleUpdateCartV2(params);
    setCartUpdateSeller([]);
    isUpdateSellerRef.current = false;
  };

  const handleUpdateSeller = (value) => {
    const resultUpdateSeller = handleUpdateCartSeller({ cartID, ...value }).then(() => {
      refetchCart().then((res) => {
        const cart = res?.data?.cart?.get;
        if (cart) {
          const newDebtAge = cart?.seller?.debtAge;
          const carrierMethodShipping = cart?.shippingConfiguration?.deliver3PartyConfig?.carrierMethod;
          const providerIDShipping = cart?.shippingConfiguration?.deliver3PartyConfig?.providerID;
          setCartUpdateSeller(cart?.seller?.warehouses);
          const convertedItems = cart?.items?.map((item) => ({
            id: item.id,
            quantity: item?.quantity,
            vat: item?.vat,
            maxDaysOfDebt: newDebtAge > 0 ? newDebtAge : null,
            customerPrice: item?.customerPrice,
            customSellerPrice: item?.price,
            productWarranty: item?.productWarranty,
            productType: item?.product?.productType,
          }));
          form.setFieldsValue({
            ...cart,
            shippingConfiguration: buildShippingConfigForForm(cart, carrierMethodShipping, providerIDShipping),
            sellerID: [cart?.seller?.code, cart?.seller?.fullName, cart?.seller?.telephone].join(" - "),
            items: convertedItems,
            deliveryDeadline: cart?.deliveryDeadline && parseIsoDateStringToMoment(cart?.deliveryDeadline),
          });
          handleUpdateCartItemV2({
            cartID,
            request: {
              cartItems: convertedItems?.map((item) => converterRequestCartItem(item)),
            },
          });
        }
      });
    });
    isUpdateSellerRef.current = true;
    if (
      resultUpdateSeller?.data?.cart?.updateCartSeller &&
      !loadingGetCart &&
      !loadingShippingProvider &&
      cartUpdateSeller?.length > 0
    ) {
      refetchShippingProvider().then((res) => {
        const servicesFee = res?.data?.shippingProvider?.servicesFee;

        if (data?.shippingType === "DELIVER_3PARTY" && idEnableNewShippingProvider) {
          const carrierMethodShipping = data?.shippingConfiguration?.deliver3PartyConfig?.carrierMethod;
          const providerIDShipping = data?.shippingConfiguration?.deliver3PartyConfig?.providerID;
          const getShippingMethods = servicesFee?.find((item) => item?.method === carrierMethodShipping);
          if (carrierMethodShipping !== null && providerIDShipping === null && servicesFee) {
            handleUpdate(
              undefined,
              getShippingMethods?.carrierMinFee,
              getShippingMethods?.carrierMaxFee,
              undefined,
              undefined
            );
          }
          const getShippingProvider = getShippingMethods?.providers?.find(
            (item) => item.id === `${providerIDShipping} - ${carrierMethodShipping}`
          );
          if (carrierMethodShipping !== null && providerIDShipping !== null && servicesFee) {
            handleUpdate(undefined, undefined, undefined, getShippingProvider?.fee, carrierMethodShipping);
          }
        }
      });
    }
  };

  const handleUpdateItem = async () => {
    const formData = await form.getFieldsValue();
    const { items } = formData;
    try {
      await handleUpdateCartItemV2({
        cartID,
        request: {
          cartItems: items?.map((item) => converterRequestCartItem(item)),
        },
      }).then((value) => {
        refetchCart().then((res) => {
          const { vat, vatAllOrder } = res?.data?.cart?.get;
          form.setFieldsValue(omit({ ...formData, vat, vatAllOrder }, ["items"]));
        });
      });
    } catch (error) {
      notify.error({ message: error.message });
    }
  };

  const { platformCode } = useGetInfoPlatform();

  const platformCodes = {
    HA: "HA",
    C_GROUP: "C_GROUP",
  };

  const handleAddNewItems = async (value) => {
    const newItems = value?.map((item, index) => ({
      productId: item.id,
      quantity: 1,
      // vat: value?.map((item) => item.vat)?.[index]
      vat:
        item.vatType === VAT_TYPE.REQUIRED ||
        (item.vatType === VAT_TYPE.OPTIONAL && cartConverter?.seller?.vatInfo?.defaultExportVat),
      customerPrice: isMaterial(item?.productType) ? 0 : item?.priceOfSeller?.price,
    }));

    const formData = form.getFieldsValue();
    try {
      await handleUpdateCartItemV2({
        cartID: cartID,
        request: {
          newItems,
        },
      }).then(() => {
        refetchCart().then((res) => {
          const cart = res?.data?.cart?.get;
          form.setFieldsValue({
            ...formData,
            vat: platformCode === platformCodes.HA ? false : true,
            items: convertItemsForForm(cart?.items, cart?.seller?.debtAge),
          });
        });
      });
    } catch (error) {
      notify.error({ message: error.message });
    }
  };

  const handleRemoveCartItem = async (id) => {
    await handleUpdateCartItemV2({ cartID, request: { removedItemIDs: [id] } }).then(() =>
      refetchCart().then((res) => {
        const cart = res?.data?.cart?.get;
        const formData = form.getFieldsValue();
        form.setFieldsValue({ ...formData, items: convertItemsForForm(cart?.items, cart?.seller?.debtAge) });
      })
    );
  };

  const handleVatAllItems = async (value) => {
    const formData = await form.getFieldsValue();

    const items = data?.items?.map((item) => {
      if (item?.product?.vat) return { ...item, vat: value };

      return {
        ...item,
        vat: value,
      };
    });
    // ?.filter((item) => item?.product?.vat === true);

    await handleUpdateCartItemV2({
      cartID,
      request: {
        cartItems: convertItemsForForm(items, cartConverter?.seller?.debtAge)?.map((item) =>
          converterRequestCartItem(item)
        ),
      },
    }).then(() => {
      refetchCart().then((res) => {
        const { vat, items, vatAllOrder, seller } = res?.data?.cart?.get;
        form.setFieldsValue({ ...formData, vat, items: convertItemsForForm(items, seller?.debtAge), vatAllOrder });
      });
    });
  };

  const hasCart = (value) => setIsEmptyCart(value);

  const handleApplyCashPaymentMethod = async () => {
    await handleUpdate({ paymentMethod: PAYMENT_METHOD.Cash, applyLoyaltyCash: true });
    await refetchCart();
  };

  const handleCancelApplyCashPaymentMethod = async () => {
    await handleUpdate({ applyLoyaltyCash: false });
    await refetchCart();
  };

  return {
    cartPermissions,
    onChangeTab,
    data,
    loading:
      loadingGetCart || loadingCartPermissions || loadingUpdateCart || loadingUpdateCartItem || loadingUpdateCartSeller,
    form,
    dataInfoAgent,
    dataInfoProductAndBill,
    handleUpdate,
    handleUpdateSeller,
    refetchCart,
    handleUpdateItem,
    handleAddNewItems,
    handleRemoveCartItem,
    handleApplyCashPaymentMethod,
    handleCancelApplyCashPaymentMethod,
    loadingUpdateCart,
    hasCart,
    isEmptyCart,
    handleVatAllItems,
    carts,
    loadingGetAllCarts,
    dataShippingProvider,
    loadingShippingProvider,
    shippingTypes,
    invalidShippingTypePopup,
    closeInvalidShippingTypePopup,
  };
};

export default useList;
